import React, { useMemo } from "react";

import { Link } from "react-router-dom";
import { StringParam, useQueryParams } from "use-query-params";
import { orderBy, uniqBy } from "lodash";

import { ReportAnswerFragment, ReportFragment } from "../../graphql/fragments";
import useGetViewer from "../../graphql/hooks/queries/useGetViewer";
import useGetReports from "../../graphql/hooks/queries/useGetReports";

import useTranslations from "../../hooks/useTranslations";
import useFuzzySearch from "../../hooks/useFuzzySearch";

import { AvatarGroup, Button, ButtonGroup, H6, Select } from "../wombat-ui";
import List from "../utils/List";
import SearchInput from "../utils/SearchInput";

import SortAscendingDirectionIcon from "../icons/SortAscendingDirectionIcon";
import SortDescendingDirectionIcon from "../icons/SortDescendingDirectionIcon";

function getEvaluationUserAnalyticsData(
  evaluations: ReportAnswerFragment[],
  userCount: number
) {
  return uniqBy(evaluations, "templateQuestion.digest").map(
    ({ templateQuestion }) => {
      const individualUsers = uniqBy(
        evaluations.filter(
          ({ templateQuestion: tq }) => templateQuestion.digest === tq.digest
        ),
        "user.id"
      ).map(({ user }) => user);

      return {
        id: templateQuestion.digest,
        label: templateQuestion.label,
        individualUsers,
        acquisitionRate: Math.round((individualUsers.length / userCount) * 100),
      };
    }
  );
}

type EvaluationAnalyticsProps = Record<string, never>;

const EvaluationAnalytics: React.FC<EvaluationAnalyticsProps> = () => {
  const t = useTranslations();
  const viewer = useGetViewer();

  const [{ q, direction, campaign }, setQuery] = useQueryParams({
    q: StringParam,
    direction: StringParam,
    campaign: StringParam,
  });

  const mountDate = useMemo(() => new Date().toISOString(), []);

  const { data: lastCheckedReportsData, loading } = useGetReports({
    variables: {
      campaignId: campaign === "ALL_CAMPAIGNS" ? undefined : campaign,
      lastCheckedAt: mountDate,
    },
  });

  const lastCheckedReports: ReportFragment[] = useMemo(() => {
    return lastCheckedReportsData?.reports?.collection || [];
  }, [lastCheckedReportsData]);

  const campaigns = useMemo(
    () =>
      [
        {
          name: t("statistics.filters.allCampaigns") as string,
          id: "ALL_CAMPAIGNS",
        },
        ...viewer?.data?.viewer?.company?.campaigns.map(({ name, id }) => ({
          name,
          id,
        })),
      ] || [],
    [viewer]
  );

  const userCount = useMemo(
    () => viewer?.data?.viewer?.company?.users?.length || 0,
    [viewer]
  );
  const userEvaluations = useMemo(
    () =>
      getEvaluationUserAnalyticsData(
        lastCheckedReports.flatMap(({ reportAnswers }) =>
          reportAnswers.filter(
            ({ templateQuestion }) =>
              templateQuestion.type === "TemplateQuestionEvaluation"
          )
        ),
        userCount
      ),
    [lastCheckedReports, userCount]
  );

  const sortedUserEvaluations = useMemo(() => {
    return orderBy(
      userEvaluations,
      "label",
      (direction?.toLowerCase() as "asc" | "desc") || "asc"
    );
  }, [userEvaluations, direction]);

  const results = useFuzzySearch(sortedUserEvaluations, q, {
    keys: ["label"],
    threshold: 0.2,
    useExtendedSearch: true,
    ignoreLocation: true,
    matchIfEmpty: true,
  });

  return (
    <List
      id={"evaluation-analytics-list"}
      data={results.map((result) => result.item)}
      renderEmptyState={() => {
        return <H6>{t("statistics.noMatchingEvaluation")}</H6>;
      }}
      listOnly
      loading={loading}
      renderAsList={(data, index) => (
        <li className="py-4" key={index}>
          <div className="flex items-center space-x-4">
            <div className="flex-1 min-w-0 ">
              <p className="text-sm font-medium truncate text-dark-100">
                {data.label}
              </p>
            </div>
            <AvatarGroup
              items={data.individualUsers.map(({ avatar, name }) => ({
                src: avatar,
                type: "user",
                alt: `${name} profile picture`,
                name: name,
              }))}
              size={8}
            />
            <div className="flex flex-col min-w-0 ">
              <p className="text-xs font-medium text-light-900">
                {t("statistics.evaluationAcquisitionRate")}
              </p>
              <p className="text-sm font-medium truncate text-dark-100">
                {data.acquisitionRate}%
              </p>
            </div>
            <Link
              to={`/statistics/evaluations/${data.id}`}
              id={`${data.id}-link`}
              className="inline-flex items-center shadow-sm px-2.5 py-0.5 border border-gray-300 text-sm leading-5 font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50"
            >
              {t("users.seeMore")}
            </Link>
          </div>
        </li>
      )}
    >
      <SearchInput
        placeholder={t("statistics.search.evaluations") as string}
        defaultValue={q}
        onChange={(q) => setQuery({ q })}
      />
      <div className="flex gap-x-2">
        <Select
          id="skill-analytics-group-select"
          label={"Group filter"}
          srOnlyLabel
          items={campaigns}
          renderItem={(i) =>
            i.id === "ALL_CAMPAIGNS"
              ? (t("statistics.filters.allCampaigns") as string)
              : i.name
          }
          searchable
          defaultValue={
            campaigns.find(({ id }) => campaign === id) || campaigns[0]
          }
          onChange={(i) => {
            setQuery({ campaign: i.id });
          }}
          searchOption={{
            keys: ["name"],
            threshold: 0.2,
            useExtendedSearch: true,
          }}
          getValue={(i) => i.name}
        />
        <ButtonGroup scale="sm">
          <Button
            icon={SortAscendingDirectionIcon}
            onClick={() => setQuery({ direction: "ASC" })}
            variant={direction === "ASC" || !direction ? "primary" : undefined}
          />
          <Button
            icon={SortDescendingDirectionIcon}
            onClick={() => setQuery({ direction: "DESC" })}
            variant={direction === "DESC" ? "primary" : undefined}
          />
        </ButtonGroup>
      </div>
    </List>
  );
};

export default React.memo(EvaluationAnalytics);
